/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';

export const OrderWizardContext = createContext({ 
  orderData: {}, 
  validationErrors: {},
  currentStep: 1,
  completedSteps: []
});

const OrderWizardProvider = ({ children }) => {
  const [orderData, setOrderData] = useState({
    // Paso 1: Información Básica
    clienteId: '',
    clienteName: '',
    businessUnit: '',
    businessUnitId: '',
    descripcion: '',
    
    // Paso 2: Información Financiera  
    fechaDesde: null,
    fechaHasta: null,
    horaDesde: '00:00',
    horaHasta: '23:59',
    moneda: 'USD',
    currencyId: 1,
    inversionBruta: '',
    inversionNeta: '',
    brokerFee: '',
    brokerFeeType: 'percentage',
    mediaFee: '',
    mediaFeeType: 'percentage',
    feeAdmin: '',
    feeAdminType: 'percentage',
    totalFees: '',
    totalFeesAmount: '',
    
    // Paso 3: Configuración Avanzada
    nivelAsignacion: '', // 'Cuenta' o 'Campañas'
    cuentaId: '', // Para compatibilidad con sistema antiguo
    campaignId: '', // *** NUEVO: ID real de campaña seleccionada ***
    accountId: '', // *** NUEVO: ID real de cuenta seleccionada ***
    selectedAccountForCampaigns: '', // *** NUEVO: Para nivel Campañas, cuenta seleccionada para cargar campañas ***
    notes: ''
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  // Función para validar cada paso
  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 1: // Información Básica
        if (!orderData.clienteId) errors.clienteId = 'Cliente es obligatorio';
        if (!orderData.businessUnit) errors.businessUnit = 'Business Unit es obligatorio';
        if (!orderData.descripcion) errors.descripcion = 'Descripción es obligatoria';
        break;
        
      case 2: // Información Financiera
        if (!orderData.fechaDesde) errors.fechaDesde = 'Fecha de inicio es obligatoria';
        if (!orderData.fechaHasta) errors.fechaHasta = 'Fecha de fin es obligatoria';
        if (!orderData.moneda) errors.moneda = 'Moneda es obligatoria';
        if (!orderData.inversionBruta || parseFloat(orderData.inversionBruta) <= 0) {
          errors.inversionBruta = 'Inversión bruta debe ser mayor a 0';
        }
        if (orderData.fechaDesde && orderData.fechaHasta && orderData.fechaDesde >= orderData.fechaHasta) {
          errors.fechaHasta = 'Fecha de fin debe ser posterior a fecha de inicio';
        }
        break;
        
      case 3: // Configuración Avanzada
        if (!orderData.nivelAsignacion) {
          errors.nivelAsignacion = 'Nivel de asignación es obligatorio';
        } else if (orderData.nivelAsignacion === 'Cuenta' && !orderData.accountId) {
          errors.accountId = 'Cuenta publicitaria es obligatoria';
        } else if (orderData.nivelAsignacion === 'Campañas' && !orderData.campaignId) {
          errors.campaignId = 'Campaña es obligatoria';
        }
        break;
        
      case 4: // Resumen Final - sin validaciones adicionales, solo verificación
        // Todas las validaciones ya se hicieron en los pasos anteriores
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  // Función para marcar un paso como completado
  const markStepAsCompleted = (step) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps([...completedSteps, step]);
    }
  };

  // Función para ir al siguiente paso
  const goToNextStep = () => {
    const errors = validateStep(currentStep);
    setValidationErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      markStepAsCompleted(currentStep);
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
      return true;
    }
    return false;
  };

  // Función para ir al paso anterior
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({});
    }
  };

  // Función para ir a un paso específico
  const goToStep = (step) => {
    if (step <= currentStep || completedSteps.includes(step - 1)) {
      setCurrentStep(step);
      setValidationErrors({});
    }
  };

  // Función para actualizar datos del formulario
  const updateOrderData = (fieldOrObject, value) => {
    if (typeof fieldOrObject === 'object') {
      // Si se pasa un objeto, actualizar múltiples campos
      setOrderData(prev => ({
        ...prev,
        ...fieldOrObject
      }));
      
      // Limpiar errores de validación para todos los campos actualizados
      const fieldsToUpdate = Object.keys(fieldOrObject);
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        fieldsToUpdate.forEach(field => {
          if (newErrors[field]) {
            newErrors[field] = undefined;
          }
        });
        return newErrors;
      });
    } else {
      // Comportamiento original para un solo campo
      setOrderData(prev => ({
        ...prev,
        [fieldOrObject]: value
      }));
      
      // Limpiar error de validación si existe
      if (validationErrors[fieldOrObject]) {
        setValidationErrors(prev => ({
          ...prev,
          [fieldOrObject]: undefined
        }));
      }
    }
  };

  // Función para resetear el wizard
  const resetWizard = () => {
    setOrderData({
      clienteId: '',
      clienteName: '',
      businessUnit: '',
      businessUnitId: '',
      descripcion: '',
      fechaDesde: null,
      fechaHasta: null,
      horaDesde: '00:00',
      horaHasta: '23:59',
      moneda: 'USD',
      currencyId: 1,
      inversionBruta: '',
      inversionNeta: '',
      brokerFee: '',
      brokerFeeType: 'percentage',
      mediaFee: '',
      mediaFeeType: 'percentage',
      feeAdmin: '',
      feeAdminType: 'percentage',
      totalFees: '',
      totalFeesAmount: '',
      nivelAsignacion: '', // 'Cuenta' o 'Campañas'
      cuentaId: '', // Para compatibilidad con sistema antiguo
      campaignId: '', // *** NUEVO: ID real de campaña seleccionada ***
      accountId: '', // *** NUEVO: ID real de cuenta seleccionada ***
      selectedAccountForCampaigns: '', // *** NUEVO: Para nivel Campañas, cuenta seleccionada para cargar campañas ***
      notes: ''
    });
    setCurrentStep(1);
    setCompletedSteps([]);
    setValidationErrors({});
    setIsEditMode(false);
  };

  // Función para cargar datos existentes (modo edición)
  const loadOrderData = (existingOrder) => {
    
    setIsEditMode(true);
    
    const formatTime = (date) => {
      if (!date) return '00:00';
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    // 1. Normalizar las fechas (sin conversión adicional si ya son Date objects)
    const dateFrom = existingOrder.fechaDesde instanceof Date ? existingOrder.fechaDesde :
                     existingOrder.fechaDesde ? new Date(existingOrder.fechaDesde) : 
                     existingOrder.dateFrom instanceof Date ? existingOrder.dateFrom :
                     existingOrder.dateFrom ? new Date(existingOrder.dateFrom) : null;
    const dateTo = existingOrder.fechaHasta instanceof Date ? existingOrder.fechaHasta :
                   existingOrder.fechaHasta ? new Date(existingOrder.fechaHasta) : 
                   existingOrder.dateTo instanceof Date ? existingOrder.dateTo :
                   existingOrder.dateTo ? new Date(existingOrder.dateTo) : null;
    
    // 2. Normalizar el nivel de asignación
    const nivelAsignacion = existingOrder.nivel === 'ACCOUNT' ? 'Cuenta' :
                            existingOrder.nivel === 'CAMPAIGN' ? 'Campañas' :
                            existingOrder.nivelAsignacion === 'ACCOUNT' ? 'Cuenta' :
                            existingOrder.nivelAsignacion === 'CAMPAIGN' ? 'Campañas' :
                            'Cuenta'; // Fallback a 'Cuenta'

    // 3. Normalizar IDs de cuenta y campaña
    const accountId = String(existingOrder.accountId || existingOrder.cuentaId || '');
    const campaignId = String(existingOrder.campaignId || (existingOrder.assignments && existingOrder.assignments[0]?.campaignId) || '');
    
    const selectedAccountForCampaigns = nivelAsignacion === 'Campañas' ? accountId : '';

    // 4. Obtener clienteId y clienteName
    let clienteId = existingOrder.client_id || existingOrder.clienteId || null;
    let clienteName = existingOrder.clienteName || existingOrder.clientName || existingOrder.cliente || '';
    
    // Asegurarnos de que clienteId sea un número válido
    if (clienteId) {
      clienteId = parseInt(clienteId, 10);
    }
    
    const mappedData = {
      // Paso 1
      clienteId: String(clienteId || ''),
      clienteName: clienteName,
      businessUnit: existingOrder.businessUnit || '',
      descripcion: existingOrder.description || existingOrder.descripcion || '',

      // Paso 2
      fechaDesde: dateFrom,
      fechaHasta: dateTo,
      horaDesde: formatTime(dateFrom),
      horaHasta: formatTime(dateTo),
      moneda: existingOrder.currency || existingOrder.moneda || 'USD',
      currencyId: existingOrder.currencyId || 1,
      inversionBruta: String(existingOrder.grossInvestment || existingOrder.inversionBruta || ''),
      inversionNeta: String(existingOrder.netInvestment || existingOrder.inversionNeta || ''),
      brokerFee: String(existingOrder.brokerFee || ''),
      mediaFee: String(existingOrder.mediaFee || ''),
      feeAdmin: String(existingOrder.adminFee || ''),

      // Paso 3
      nivelAsignacion: nivelAsignacion,
      accountId: accountId,
      campaignId: campaignId,
      selectedAccountForCampaigns: selectedAccountForCampaigns,
      
      // Deprecado pero mantenido por si acaso
      cuentaId: accountId,
      notes: existingOrder.notes || existingOrder.description || ''
    };

    
    setOrderData(mappedData);
    setCurrentStep(1);
    setCompletedSteps([1, 2, 3]); 
    setValidationErrors({});
  };

  // Verificar si todos los pasos están completados
  const isWizardComplete = () => {
    return completedSteps.includes(1) && completedSteps.includes(2) && completedSteps.includes(3) && completedSteps.includes(4);
  };

  const value = {
    orderData,
    setOrderData,
    currentStep,
    setCurrentStep,
    completedSteps,
    validationErrors,
    setValidationErrors,
    isEditMode,
    validateStep,
    markStepAsCompleted,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updateOrderData,
    resetWizard,
    loadOrderData,
    isWizardComplete
  };

  React.useEffect(() => {
    // Solo actualizar cuando realmente sea necesario, no en cada render
    window.wizardContext = {
      orderData,
      setOrderData,
      currentStep,
      setCurrentStep,
      completedSteps,
      validationErrors,
      setValidationErrors,
      isEditMode,
      validateStep,
      markStepAsCompleted,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      updateOrderData,
      resetWizard,
      loadOrderData,
      isWizardComplete
    };
    
    return () => {
      window.wizardContext = null;
    };
  }, [orderData, currentStep, completedSteps, validationErrors, isEditMode, validateStep, markStepAsCompleted, goToNextStep, goToPreviousStep, goToStep, updateOrderData, isWizardComplete]); // *** SIN dependencias para evitar re-ejecutarse constantemente ***

  return (
    <OrderWizardContext.Provider value={value}>
      {children}
    </OrderWizardContext.Provider>
  );
};

OrderWizardProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useOrderWizardContext = () => useContext(OrderWizardContext);

export default OrderWizardProvider; 